/**
 * La case « Référence constructeur » d'une ligne de gamme, avec sa liste de suggestions.
 *
 * « Parfois je ne connais pas la référence entière » : on tape le DÉBUT (`6.3572`, `7.4519`), la liste
 * montre les références du tarif du fabricant qui commencent ainsi — référence, désignation, famille,
 * prix — et un clic (ou ↓ puis Entrée) pose la référence entière sur la ligne. Même présentation que la
 * recherche de l'écran « Tarifs ».
 *
 * Ce composant ne décide de rien : il montre ce que le serveur rend et dit au parent ce qui a été choisi.
 * Il propose, il ne pose jamais rien d'office ; une case que l'on quitte sans choisir garde ce qui a été
 * tapé, comme avant.
 *
 * La liste est rendue dans `document.body` et positionnée sur la case : le tableau des lignes défile
 * (`overflow`) et couperait une liste placée dans la cellule. Elle suit la case quand on défile et se place
 * au-dessus quand il n'y a pas la place en dessous.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { api, type ReferenceDuTarif, type ReferencesDuTarif } from '../api/client';
import { peutSuggererReference, suggestionsAMontrer } from '../domaine/gammes-edition';
import { eurosFr } from '../domaine/tarifs';

/** La recherche part après cette pause de frappe : une requête par touche serait du bruit. */
const PAUSE_DE_FRAPPE_MS = 220;
const LARGEUR_MIN = 360;
const LARGEUR_MAX = 780;
/** En dessous de cette place libre, la liste passe AU-DESSUS de la case. */
const HAUTEUR_LISTE = 330;

interface Emplacement {
  readonly gauche: number;
  readonly largeur: number;
  readonly haut?: number;
  readonly bas?: number;
}

export function ChampReference({
  gammeId,
  fournisseurId,
  machineId,
  libelle = 'Référence constructeur',
  autoFocus = false,
  valeur,
  placeholder,
  desactive,
  onChange,
  onChoisir,
  onQuitte,
}: {
  /** Les références du tarif du fabricant de CETTE gamme (éditeur de gamme)… */
  gammeId?: string;
  /** … ou celles des tarifs de CE fournisseur (écran des conditions d'achat). L'un ou l'autre, jamais les deux. */
  fournisseurId?: string;
  /** … ou celles du tarif de la MARQUE de cette machine (saisie d'un article sur un bon, quand la machine n'a pas de gamme). */
  machineId?: string;
  /** Le nom de la case pour les lecteurs d'écran. */
  libelle?: string;
  autoFocus?: boolean;
  valeur: string;
  placeholder: string;
  desactive: boolean;
  onChange: (valeur: string) => void;
  /** Une ligne de la liste a été choisie : le parent pose la référence entière sur la ligne. */
  onChoisir: (choix: ReferenceDuTarif) => void;
  /** La case est quittée : le parent lance ses aides (désignation, prix, équivalent). */
  onQuitte?: () => void;
}) {
  const champ = useRef<HTMLInputElement>(null);
  const idListe = useId();
  const [ouvert, setOuvert] = useState(false);
  const [reponse, setReponse] = useState<ReferencesDuTarif | null>(null);
  const [actif, setActif] = useState(-1);
  const [emplacement, setEmplacement] = useState<Emplacement | null>(null);
  // Une réponse tardive ne remplace jamais celle d'une frappe plus récente.
  const numeroDeRequete = useRef(0);

  useEffect(() => {
    if (!ouvert) return;
    if (!peutSuggererReference(valeur)) {
      setReponse(null);
      return;
    }
    const numero = ++numeroDeRequete.current;
    const minuteur = window.setTimeout(() => {
      // Sans gamme, fournisseur ni machine, il n'y a pas de tarif où chercher : la saisie reste entière, sans aide.
      const recherche: Promise<ReferencesDuTarif> | null = fournisseurId
        ? api.referencesFournisseur(fournisseurId, valeur.trim())
        : gammeId
          ? api.maintenanceReferencesTarif(gammeId, valeur.trim())
          : machineId
            ? api.referencesTarifMachine(machineId, valeur.trim())
            : null;
      if (!recherche) {
        setReponse(null);
        return;
      }
      recherche
        .then((rendue) => {
          if (numero !== numeroDeRequete.current) return;
          setReponse(rendue);
          setActif(-1);
        })
        // Une aide : si elle échoue, la saisie manuelle reste entière.
        .catch(() => {
          if (numero === numeroDeRequete.current) setReponse(null);
        });
    }, PAUSE_DE_FRAPPE_MS);
    return () => window.clearTimeout(minuteur);
  }, [valeur, ouvert, gammeId, fournisseurId, machineId]);

  const aMontrer = ouvert ? suggestionsAMontrer(valeur, reponse) : null;
  const visible = aMontrer !== null;
  const nbLignes = aMontrer?.items.length ?? 0;

  const placer = useCallback(() => {
    const el = champ.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const largeur = Math.max(LARGEUR_MIN, Math.min(LARGEUR_MAX, window.innerWidth - 24));
    const gauche = Math.max(8, Math.min(r.left, window.innerWidth - largeur - 8));
    const placeEnDessous = window.innerHeight - r.bottom;
    const auDessus = placeEnDessous < HAUTEUR_LISTE && r.top > placeEnDessous;
    setEmplacement(
      auDessus
        ? { gauche, largeur, bas: window.innerHeight - r.top + 2 }
        : { gauche, largeur, haut: r.bottom + 2 },
    );
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    placer();
    // `capture` : le défilement de la colonne, pas seulement celui de la page.
    window.addEventListener('scroll', placer, true);
    window.addEventListener('resize', placer);
    return () => {
      window.removeEventListener('scroll', placer, true);
      window.removeEventListener('resize', placer);
    };
  }, [visible, nbLignes, placer]);

  // La ligne parcourue au clavier reste visible dans la liste.
  useEffect(() => {
    if (actif >= 0) document.getElementById(`${idListe}-${actif}`)?.scrollIntoView({ block: 'nearest' });
  }, [actif, idListe]);

  const choisir = (choix: ReferenceDuTarif) => {
    setOuvert(false);
    setReponse(null);
    onChoisir(choix);
  };

  const surTouche = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!aMontrer) return;
    const n = aMontrer.items.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActif((a) => (n === 0 ? -1 : (a + 1) % n));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((a) => (n === 0 ? -1 : a <= 0 ? n - 1 : a - 1));
    } else if (e.key === 'Enter') {
      const choix = aMontrer.items[actif];
      // Sans ligne parcourue, Entrée garde son rôle habituel : on ne choisit jamais à la place de l'utilisateur.
      if (choix) {
        e.preventDefault();
        choisir(choix);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOuvert(false);
    }
  };

  const avecPrix = aMontrer?.items.some((i) => i.prix !== undefined) ?? false;
  const plusieursTarifs = (aMontrer?.tarifs.length ?? 0) > 1;

  return (
    <>
      <input
        ref={champ}
        type="text"
        role="combobox"
        aria-label={libelle}
        aria-expanded={visible}
        aria-autocomplete="list"
        aria-controls={idListe}
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={valeur}
        disabled={desactive}
        onChange={(e) => {
          onChange(e.target.value);
          setOuvert(true);
        }}
        onKeyDown={surTouche}
        onBlur={() => {
          setOuvert(false);
          onQuitte?.();
        }}
      />
      {aMontrer &&
        emplacement &&
        createPortal(
          // Un clic dans la liste ne doit PAS faire perdre le focus à la case : elle se refermerait avant le clic.
          <div
            id={idListe}
            role="listbox"
            className="suggestions-reference"
            style={{
              left: emplacement.gauche,
              width: emplacement.largeur,
              ...(emplacement.haut === undefined ? {} : { top: emplacement.haut }),
              ...(emplacement.bas === undefined ? {} : { bottom: emplacement.bas }),
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {aMontrer.items.length === 0 ? (
              <div className="suggestions-vide">
                Aucune référence du tarif ne correspond à « {valeur.trim()} ».
              </div>
            ) : (
              <div className="suggestions-defilement">
                <table>
                  <thead>
                    <tr>
                      <th>Référence</th>
                      <th>Désignation</th>
                      <th>Famille</th>
                      {plusieursTarifs && <th>Tarif</th>}
                      {avecPrix && <th className="droite">Prix du tarif HT</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {aMontrer.items.map((s, i) => (
                      <tr
                        key={`${s.tarif}-${s.reference}`}
                        id={`${idListe}-${i}`}
                        role="option"
                        aria-selected={i === actif}
                        className={i === actif ? 'actif' : undefined}
                        onMouseEnter={() => setActif(i)}
                        onClick={() => choisir(s)}
                      >
                        <td>
                          <code>{s.reference}</code>
                        </td>
                        <td>{s.description}</td>
                        <td>{s.famille ?? ''}</td>
                        {plusieursTarifs && <td>{s.tarif}</td>}
                        {avecPrix && <td className="droite">{s.prix === undefined ? '' : eurosFr(s.prix)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="suggestions-pied">
              {aMontrer.items.length === 0
                ? 'Continuez à taper, ou saisissez la référence entière.'
                : aMontrer.total > aMontrer.items.length
                  ? `${aMontrer.items.length} références sur ${aMontrer.total} : continuez à taper pour préciser.`
                  : `${aMontrer.total} référence${aMontrer.total > 1 ? 's' : ''}.`}{' '}
              {!plusieursTarifs && aMontrer.tarifs[0] ? <span>Tarif : {aMontrer.tarifs[0]}</span> : null}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
